import dayjs from 'dayjs'

import { SERVER_LOCATION } from '../config'
import { getStoredUser, updateStoredUser } from './auth'
import { handleUnauthorized } from "./utils"
import api, { getEnterpriseId, buildHeaders } from '../lib/api'
import { loader } from '../lib/loader'


const normalizeCollection = (data) => {
	if (Array.isArray(data)) {
		return data
	}

	if (Array.isArray(data?.results)) {
		return data.results
	}

	if (Array.isArray(data?.data)) {
		return data.data
	}

	return []
}

const getErrorMessage = (payload, fallback) => {
	if (!payload) {
		return fallback
	}

	if (typeof payload === 'string') {
		return payload
	}

	if (typeof payload.error === 'string') {
		return payload.error
	}

	if (typeof payload.detail === 'string') {
		return payload.detail
	}

	return fallback
}

const fetchKwik = async (path, options = {}) => {
	const response = await fetch(`${SERVER_LOCATION}/api${path}`, {
		credentials: 'same-origin',
		headers: buildHeaders(options.headers),
		...options,
	})

	if (response.status === 204) return null

	const payload = await response.json().catch(() => null)

	if (response.status === 401) {
		handleUnauthorized()
		return null
	}

	if (response.status === 429) {
		const error = new Error(getErrorMessage(payload, 'Muitas requisições. Por favor, tente novamente mais tarde.'))
		error.status = response.status
		throw error
	}

	if (!response.ok) {
		const error = new Error(getErrorMessage(payload, 'Não foi possível carregar os dados do Scout.'))
		error.status = response.status
		throw error
	}

	return payload
}

const fetchKwikOptional = async (path, options = {}) => {
	try {
		return await fetchKwik(path, options)
	} catch (error) {
		if (error?.name === 'AbortError') {
			throw error
		}

		return null
	}
}

const decodeHtmlEntities = (value = '') => {
	return String(value)
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&#39;/g, "'")
		.replace(/&quot;/g, '"')
}

const stripHtml = (value = '') => String(value).replace(/<[^>]+>/g, '')

const getObjectPayload = (value) => value && typeof value === 'object' ? value : {}

const getTextFromMessagePayload = (value) => {
	if (typeof value === 'string' && value.trim()) {
		return value
	}

	const payload = getObjectPayload(value)

	if (typeof payload.message === 'string' && payload.message.trim()) {
		return payload.message
	}

	if (typeof payload.conversation === 'string' && payload.conversation.trim()) {
		return payload.conversation
	}

	if (typeof payload.extendedTextMessage?.text === 'string' && payload.extendedTextMessage.text.trim()) {
		return payload.extendedTextMessage.text
	}

	if (payload.message && typeof payload.message === 'object') {
		const nestedMessageText = getTextFromMessagePayload(payload.message)
		if (nestedMessageText) {
			return nestedMessageText
		}
	}

	return ''
}

const getProtocolContainer = (value) => {
	const payload = getObjectPayload(value)

	if (
		payload.protocolMessage
		|| payload.editedMessage
		|| payload.conversation
		|| payload.extendedTextMessage
	) {
		return payload
	}

	return getObjectPayload(payload.message)
}

export const getProtocolMessagePayload = (value) => {
	const payload = getProtocolContainer(value)
	const protocolMessage = getObjectPayload(payload.protocolMessage)

	if (Object.keys(protocolMessage).length) {
		return protocolMessage
	}

	if (payload.key && (payload.type !== undefined || payload.editedMessage)) {
		return payload
	}

	return getObjectPayload(payload.editedMessage?.message?.protocolMessage)
}

const normalizeProtocolMessageType = (value) => String(value ?? '').trim().toUpperCase()

const isRevokeProtocolMessageType = (value) => {
	const normalizedType = normalizeProtocolMessageType(value)
	return normalizedType === '0' || normalizedType === 'REVOKE'
}

const isMessageEditProtocolMessageType = (value) => {
	const normalizedType = normalizeProtocolMessageType(value)
	return normalizedType === '14' || normalizedType === 'MESSAGE_EDIT'
}

export const getProtocolEditedMessageText = (value) => {
	const payload = getProtocolContainer(value)
	const rootText = getTextFromMessagePayload(payload)

	if (rootText) {
		return rootText
	}

	return getTextFromMessagePayload(getProtocolMessagePayload(payload)?.editedMessage)
}

export const isEditedProtocolMessagePayload = (value) => {
	const protocolMessage = getProtocolMessagePayload(value)

	return Boolean(
		getProtocolEditedMessageText(value)
		|| (
			isMessageEditProtocolMessageType(protocolMessage?.type)
			&& protocolMessage?.editedMessage
			&& typeof protocolMessage.editedMessage === 'object'
		),
	)
}

export const isDeletedProtocolMessagePayload = (value) => {
	const protocolMessage = getProtocolMessagePayload(value)
	const payload = getObjectPayload(value)

	return Boolean(
		!isEditedProtocolMessagePayload(value)
		&& (
			isRevokeProtocolMessageType(protocolMessage?.type)
			|| normalizeProtocolMessageType(payload?.type) === 'DELETED'
			|| String(payload?.status || '').toUpperCase() === 'DELETED'
		),
	)
}

const normalizeStatusValue = (value = '') => {
	return String(value || '')
		.trim()
		.toLowerCase()
}

export const getUsers = async () => {
	const enterpriseId = getEnterpriseId()
	const res = await fetchKwik(`/enterprises/${enterpriseId}/users/`)
	return res
}

export const getUsersEmails = async () => {
	const enterpriseId = getEnterpriseId()
	const res = await fetchKwik(`/enterprises/${enterpriseId}/users/get_users_emails/`)
	return res
}

export const createAccount = async (data) => {
	loader.on()
	const enterpriseId = getEnterpriseId()
	const response = await api.post(`/enterprises/${enterpriseId}/scout_accounts/`, {
		created_by_id: data.owner,
		email: data.email,
		name: data.name,
		phone_number: data.phone.replace('+', '')
	})
	loader.off()
	return response
}

export const updateAccount = async (account, data) => {
	loader.on()
	const enterpriseId = getEnterpriseId()
	const response = await api.patch(`/enterprises/${enterpriseId}/scout_accounts/${account.id}/`, {
		name: data.name,
		email: data.email,
		owner_id: data.owner,
	})
	loader.off()
	return response
}

export const updateAccountOwner = async (account, owner_id) => {
	loader.on()
	const enterpriseId = getEnterpriseId()
	const response = await api.patch(`/enterprises/${enterpriseId}/scout_accounts/${account.id}/`, {
		owner_id: owner_id
	})
	loader.off()
	return response
}

export const createUser = async (data) => {
	loader.on()
	try {
		const enterpriseId = getEnterpriseId()
		const response = await api.post(`/enterprises/${enterpriseId}/scout_user/`, {
			name: data.name,
			email: data.email,
			telephone: data.telephone,
		})
		return response
	} finally {
		loader.off()
	}
}

export const getEnterprise = async () => {
	const enterpriseId = getEnterpriseId()
	const res = await fetchKwik(`/enterprises/${enterpriseId}/`)
	return res
}

const mergeEnterpriseIntoUser = (user, enterprise) => {
	if (!user || !enterprise) {
		return user
	}

	return {
		...user,
		profile: {
			...(user.profile || {}),
			enterprise: {
				...(user.profile?.enterprise || {}),
				...enterprise,
			},
		},
	}
}

export const updateScoutAdminSettings = async (data) => {
	loader.on()
	try {
		const enterpriseId = getEnterpriseId()
		const rawResponse = await api.patch(`/enterprises/${enterpriseId}/scout_admin_settings/`, data)
		const response = rawResponse?.data || rawResponse
		const updatedUser = mergeEnterpriseIntoUser(response?.user, response?.enterprise)

		if (updatedUser) {
			updateStoredUser(updatedUser)
 		}

		return {
			...response,
			user: updatedUser || response?.user,
		}
	} finally {
		loader.off()
	}
}

export const resolveScoutAccountConnectionStatus = (account = {}) => {
	const rawStatus = normalizeStatusValue(account?.connection_state ?? account?.status)

	if (rawStatus === 'reconnecting') {
		return account?.connection_validated ? 'open' : 'close'
	}

	return rawStatus
}

export const isScoutAccountActive = (account) => {
	const resolvedStatus = resolveScoutAccountConnectionStatus(account)

	return ['open', 'opened', 'connected', 'online'].includes(resolvedStatus) || Boolean(account?.isActive)
}


export const getScoutContext = () => {
	const user = getStoredUser()
	const enterpriseId = getEnterpriseId()

	return {
		user,
		enterpriseId,
	}
}

export const getDefaultScoutDate = () => dayjs().subtract(1, 'day').format('YYYY-MM-DD')

export const getDefaultScoutDatePreset = () => 'yesterday'

export const getDefaultScoutDatePresetValue = () => dayjs().subtract(1, 'day').format('YYYY-MM-DD')

export const normalizeScoutDate = (value) => {
	const parsedDate = dayjs(value)
	const today = dayjs().startOf('day')

	if (!parsedDate.isValid() || !parsedDate.startOf('day').isBefore(today)) {
		return getDefaultScoutDatePresetValue()
	}

	return parsedDate.format('YYYY-MM-DD')
}

export const listScoutDateOptions = () => {
	const yesterday = dayjs().subtract(1, 'day')
	const lastWeek = dayjs().subtract(1, 'week')
	const lastMonth = dayjs().subtract(1, 'month')

	return [
		{
			key: 'yesterday',
			value: yesterday.format('YYYY-MM-DD'),
			startDate: yesterday.format('YYYY-MM-DD'),
			endDate: yesterday.format('YYYY-MM-DD'),
			label: 'Ontem',
		},
		{
			key: 'last-7-days',
			value: yesterday.format('YYYY-MM-DD'),
			startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
			endDate: yesterday.format('YYYY-MM-DD'),
			label: 'Últimos 7 dias',
		},
		{
			key: 'last-30-days',
			value: yesterday.format('YYYY-MM-DD'),
			startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
			endDate: yesterday.format('YYYY-MM-DD'),
			label: 'Últimos 30 dias',
		},
		{
			key: 'last-week',
			value: lastWeek.endOf('week').format('YYYY-MM-DD'),
			startDate: lastWeek.startOf('week').format('YYYY-MM-DD'),
			endDate: lastWeek.endOf('week').format('YYYY-MM-DD'),
			label: 'Semana passada',
		},
		{
			key: 'this-month',
			value: yesterday.format('YYYY-MM-DD'),
			startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
			endDate: yesterday.format('YYYY-MM-DD'),
			label: 'Este mês',
		},
		{
			key: 'last-month',
			value: lastMonth.endOf('month').format('YYYY-MM-DD'),
			startDate: lastMonth.startOf('month').format('YYYY-MM-DD'),
			endDate: lastMonth.endOf('month').format('YYYY-MM-DD'),
			label: 'Mês passado',
		},
		{
			key: 'last-year',
			value: yesterday.format('YYYY-MM-DD'),
			startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
			endDate: yesterday.format('YYYY-MM-DD'),
			label: 'Último ano',
		},
	]
}

export const listScoutAccounts = async () => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}

	const [data] = await Promise.all([
		fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/`),
	])

	const normalizedAccounts = normalizeCollection(data).map(normalizeScoutAccount)

	return normalizedAccounts.sort((first, second) => Number(Boolean(second.isActive)) - Number(Boolean(first.isActive)))
}

export const SCOUT_ACCOUNT_FILTER_OPTIONS = [
	{ value: 'name', label: 'Nome' },
	{ value: 'phone_number', label: 'Número' },
]

export const DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE = 10

const normalizeAccountFilterText = (value = '') => (
	String(value)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
)

const filterScoutAccountsLocally = (accounts = [], filterBy = 'name', filter = '') => {
	const filterValue = String(filter || '').trim()
	if (!filterValue) return accounts

	if (filterBy === 'phone_number') {
		const phoneFilter = filterValue.replace(/\D/g, '')
		if (!phoneFilter) return []
		return accounts.filter((account) => String(account?.phone_number || '').replace(/\D/g, '').includes(phoneFilter))
	}

	const nameFilter = normalizeAccountFilterText(filterValue)
	return accounts.filter((account) => normalizeAccountFilterText(account?.name).includes(nameFilter))
}

export const normalizeScoutAccount = (account = {}) => {
	return {
		...account,
		avatarUrl: account?.picture_url || '',
		isActive: isScoutAccountActive(account),
	}
}

export const listScoutAccountsPage = async ({
	page = 1,
	pageSize = DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
	filterBy = 'name',
	filter = '',
} = {}) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}

	const params = new URLSearchParams({
		page: String(page),
		page_size: String(pageSize),
		filter_by: String(filterBy || 'name'),
	})

	const normalizedFilter = String(filter || '').trim()
	if (normalizedFilter) {
		params.set('filter', normalizedFilter)
	}

	const data = await fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/?${params.toString()}`)
	const results = filterScoutAccountsLocally(
		normalizeCollection(data).map(normalizeScoutAccount),
		filterBy,
		normalizedFilter,
	)

	return {
		results,
		count: Number(data?.count ?? results.length),
		next: data?.next || null,
		previous: data?.previous || null,
		hasMore: Boolean(data?.next),
		nextPage: data?.next ? Number(page) + 1 : null,
		page: Number(page),
	}
}

export const getScoutAccountDetails = async ({ accountId, live = true }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId) {
		return null
	}

	const params = new URLSearchParams()

	if (live) {
		params.set('live', '1')
	}

	const query = params.toString()
	const data = await fetchKwik(
		`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/${query ? `?${query}` : ''}`,
	)

	return normalizeScoutAccount(data)
}

export const getScoutEnterprise = async () => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}

	return fetchKwik(`/enterprises/${enterpriseId}/?scout=True`)
}

const SCOUT_PAGE_ACCESS_STORAGE_PREFIX = 'kwik_scout_page_access'

const canUseLocalStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage)

const getScoutPageAccessStorageKey = (enterpriseId) => `${SCOUT_PAGE_ACCESS_STORAGE_PREFIX}_${enterpriseId}`

const getAccessDate = (value) => {
	if (!value) {
		return ''
	}

	const parsedDate = dayjs(value)
	return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : ''
}

const getTodayAccessDate = () => dayjs().format('YYYY-MM-DD')

const wasScoutPageAccessRegisteredToday = (enterpriseId, lastPageAccessAt) => {
	const today = getTodayAccessDate()

	if (getAccessDate(lastPageAccessAt) === today) {
		if (canUseLocalStorage()) {
			window.localStorage.setItem(getScoutPageAccessStorageKey(enterpriseId), today)
		}
		return true
	}

	if (!canUseLocalStorage()) {
		return false
	}

	return window.localStorage.getItem(getScoutPageAccessStorageKey(enterpriseId)) === today
}

export const registerScoutPageAccess = async (enterprise) => {
	const { enterpriseId } = getScoutContext()
	const resolvedEnterpriseId = enterprise?.id || enterpriseId

	if (!resolvedEnterpriseId) {
		return null
	}

	if (wasScoutPageAccessRegisteredToday(resolvedEnterpriseId, enterprise?.scout_last_page_access_at)) {
		return null
	}

	const response = await fetchKwikOptional(`/enterprises/${resolvedEnterpriseId}/scout_accounts/page_access/`, {
		method: 'POST',
	})

	if (response?.scout_last_page_access_at && canUseLocalStorage()) {
		window.localStorage.setItem(
			getScoutPageAccessStorageKey(resolvedEnterpriseId),
			getAccessDate(response.scout_last_page_access_at) || getTodayAccessDate(),
		)
	}

	return response
}

export const getSupportUrl = async () => {
	return fetchKwik(`/configurable_urls/?name=suporte`)
}

export const getDemoUrl = async () => {
	return fetchKwik(`/configurable_urls/?name=scout_demo`)
}

export const checkoutStripePlan = async ({ planId, checkoutMode = "subscribe", withAnnualDiscount = false }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}

	if (!planId) {
		throw new Error('Plano não definido.')
	}

	const params = new URLSearchParams({
		plan_id: String(planId),
		checkout_mode: String(checkoutMode),
		annual_discount: Boolean(withAnnualDiscount),
	})

	return fetchKwik(`/enterprises/${enterpriseId}/stripe_checkout/?${params.toString()}`)
}

export const listStripePlans = async () => {
	const plans = await fetchKwik('/plans/?external_provider=STRIPE')
	if (!Array.isArray(plans)) {
		return []
	}
	return plans
}

export const cancelStripeSubscription = async () => {
	const { enterpriseId } = getScoutContext()
	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}
	return fetchKwik(`/enterprises/${enterpriseId}/cancel_subscription/`, {
		method: 'POST',
	})
}

export const reactivateStripeSubscription = async () => {
	const { enterpriseId } = getScoutContext()
	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}
	return fetchKwik(`/enterprises/${enterpriseId}/reactivate_subscription/`, {
		method: 'POST',
	})
}

export const payOverdue = async () => {
	const { enterpriseId } = getScoutContext()
	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}
	return fetchKwik(`/enterprises/${enterpriseId}/pay_overdue/`, {
		method: 'POST',
	})
}

export const changeStripePlan = async ({ planId, withAnnualDiscount = false }) => {
	const { enterpriseId } = getScoutContext()
	if (!enterpriseId) {
		throw new Error('Não foi possível identificar a empresa do usuário logado.')
	}
	if (!planId) {
		throw new Error('Plan id is required.')
	}
	return fetchKwik(`/enterprises/${enterpriseId}/change_plan/`, {
		method: 'POST',
		body: JSON.stringify({
			plan_id: String(planId),
			annual_discount: Boolean(withAnnualDiscount)
		}),
	})
}

export const activateSubscription = async (planId) => {
	const { enterpriseId } = getScoutContext()
	if (!enterpriseId) {
		throw new Error("Não foi possível identificar a empresa do usuário logado.")
	}
	return fetchKwik(`/enterprises/${enterpriseId}/activate_manual_subscription/`, {
		method: "POST",
		body: JSON.stringify({
			plan_id: planId
		})
	})
}

export const getScoutAccountQr = async ({ accountId }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId) {
		throw new Error('Não foi possível gerar o QR Code desta conta.')
	}

	return fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/getqr/`)
}

export const checkScoutAccountConnection = async ({ accountId }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId) {
		throw new Error('Não foi possível verificar a conexão desta conta.')
	}

	return fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/checkconnection/`)
}

export const renotifyScoutAccount = async ({ accountId, email }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId) {
		throw new Error('Não foi possível reenviar o convite desta conta.')
	}

	const url = `/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/renotify/`

	if (email) {
		return fetchKwik(url, {
			method: 'POST',
			body: JSON.stringify({ email }),
		})
	}

	return fetchKwik(url)
}

const buildParams = ({ accountId, conversationId, date, dateStart, dateEnd }) => {
	const params = new URLSearchParams()

	if (dateStart && dateEnd) {
		params.set('date_start', String(dateStart))
		params.set('date_end', String(dateEnd))
	} else if (date) {
		params.set('date', String(date))
	}

	if (accountId) {
		params.set('account_id', String(accountId))
	}

	if (conversationId) {
		params.set('conversation_id', String(conversationId))
	}

	return params
}

const getPreviousPeriod = (dateStart, dateEnd) => {
	const startDate = new Date(dateStart)
	const endDate = new Date(dateEnd)
	
	const duration = endDate - startDate
	
	const previousEndDate = new Date(startDate - 1)
	const previousStartDate = new Date(previousEndDate - duration)

	return {
		prevDateStart: previousStartDate.toISOString().split("T")[0],
		prevDateEnd: previousEndDate.toISOString().split("T")[0]
	}
}

const getChange = (current, previous) => {
	if (previous === 0) {
		if (current === 0) return 0
		return 100
	}

	return Math.round(((current - previous) / previous) * 100)
}

export const getScoutAccountAnalytics = async ({ accountId, date, dateStart, dateEnd }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || (!date && !(dateStart && dateEnd))) {
		return null
	}

	const selectedParams = buildParams({ accountId, date, dateStart, dateEnd })
	
	let previousParams = null

	if (dateStart && dateEnd) {
		const { prevDateStart, prevDateEnd } = getPreviousPeriod(dateStart, dateEnd)
		previousParams = buildParams({
			accountId,
			dateStart: prevDateStart,
			dateEnd: prevDateEnd,
		})
	} else if (date) {
		const d = new Date(date)
		d.setDate(d.getDate() - 1)
		const prevDate = d.toISOString().split("T")[0]
		previousParams = buildParams({
			accountId,
			date: prevDate,
		})
	}

	const [selectedAnalytics, previousAnalytics] = await Promise.all([
		fetchKwik(`/enterprises/${enterpriseId}/scout_analytics/account/?${selectedParams}`),
		previousParams
			? fetchKwik(`/enterprises/${enterpriseId}/scout_analytics/account/?${previousParams}`)
			: Promise.resolve(null),
	])

	return {
		...selectedAnalytics,
		groupsChange: getChange(
			selectedAnalytics?.groups ?? 0,
			previousAnalytics?.groups ?? 0
		),
		conversationsChange: getChange(
			selectedAnalytics?.conversations ?? 0,
			previousAnalytics?.conversations ?? 0
		),
		totalMessagesChange: getChange(
			(selectedAnalytics?.conversation_messages ?? 0) + (selectedAnalytics?.group_messages ?? 0),
			(previousAnalytics?.conversation_messages ?? 0) + (previousAnalytics?.group_messages ?? 0)
		),
		sentimentChange: getChange(
			selectedAnalytics?.sentiment ?? 0,
			previousAnalytics?.sentiment ?? 0
		),
	}
}

export const getScoutConversationAnalytics = async ({ accountId, conversationId, date, signal }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId || !conversationId || !date) {
		return null
	}

	return fetchKwik(
		`/enterprises/${enterpriseId}/scout_analytics/conversation/?date=${encodeURIComponent(date)}&account_id=${encodeURIComponent(accountId)}&conversation_id=${encodeURIComponent(conversationId)}`,
		{ signal },
	)
}

export const exportScoutConversationMessages = async ({
	accountId,
	conversationId,
	email,
	dateStart,
	dateEnd,
	format = 'pdf',
	numberText,
}) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId || !conversationId || !email || !dateStart || !dateEnd) {
		throw new Error('Preencha os dados para exportar a conversa.')
	}

	return fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/exportmessages/${encodeURIComponent(conversationId)}/`, {
		method: 'POST',
		body: JSON.stringify({
			email,
			dateini: dayjs(dateStart).unix(),
			dateend: dayjs(dateEnd).unix(),
			type: format,
			numbertext: numberText,
		}),
	})
}

export const getScoutConversationExportEmailStatus = async ({ accountId, statusKey, signal }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId || !statusKey) {
		throw new Error('Não foi possível acompanhar a exportação.')
	}

	return fetchKwik(
		`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/exportmessages_status/${encodeURIComponent(statusKey)}/`,
		{ signal },
	)
}

export const listScoutConversations = async ({ accountId, limit = 100, skip = 0, sort = 'desc' }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId) {
		return []
	}

	const data = await fetchKwik(
		`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/conversations/?limit=${encodeURIComponent(limit)}&skip=${encodeURIComponent(skip)}&sort=${encodeURIComponent(sort)}`,
	)

	return normalizeCollection(data).map((conversation) => ({
		...conversation,
		avatarUrl: conversation?.profile_picture || '',
	}))
}

const stripAccents = (value = '') => {
	return String(value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

const normalizeSearchText = (value = '') => {
	return stripAccents(String(value || '').trim()).toLowerCase()
}

export const searchScoutMessages = async ({ text }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !String(text || '').trim()) {
		return {}
	}

	const normalizedText = normalizeSearchText(text)

	return fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/text_search/`, {
		method: 'POST',
		body: JSON.stringify({
			text: normalizedText,
		}),
	})
}

const sortMessagesAscending = (messages = []) => {
	return [...messages].sort((first, second) => {
		return Number(first?.timestamp || 0) - Number(second?.timestamp || 0)
	})
}

export const listScoutMessages = async ({
	accountId,
	conversationId,
	conversationIds = [],
	limit = 20,
	skip = 0,
	sort = '-messageTimestamp',
	slimResponse = 1,
	chatMessageId,
	signal,
}) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId || !conversationId) {
		return []
	}

	const params = new URLSearchParams({
		limit: String(limit),
		skip: String(skip),
		sort: String(sort),
		slimResponse: String(slimResponse),
	})

	if (chatMessageId) {
		params.set('chatMessageId', String(chatMessageId))
	}

	const normalizedConversationIds = Array.from(new Set(
		[conversationId, ...conversationIds]
			.map((nextId) => String(nextId || '').trim())
			.filter(Boolean),
	))

	if (normalizedConversationIds.length > 1) {
		params.set('conversationIds', normalizedConversationIds.join(','))
	}

	const data = await fetchKwik(
		`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/messages/${encodeURIComponent(conversationId)}/?${params.toString()}`,
		{ signal },
	)

	return sortMessagesAscending(normalizeCollection(data))
}

export const getScoutMessageById = async ({
	accountId,
	conversationId,
	messageId,
}) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId || !conversationId || !messageId) {
		return null
	}

	const params = new URLSearchParams({
		limit: '1',
		skip: '0',
		slimResponse: '0',
		chatMessageId: String(messageId),
	})

	const data = await fetchKwik(
		`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/messages/${encodeURIComponent(conversationId)}/?${params.toString()}`,
	)

	return normalizeCollection(data)[0] || null
}

export const getScoutConversationExport = async ({ accountId, date, conversationId, signal }) => {
	const { enterpriseId } = getScoutContext()

	if (!enterpriseId || !accountId || !date || !conversationId) {
		return []
	}

	const data = await fetchKwikOptional(
		`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/exporttext/${encodeURIComponent(date)}/${encodeURIComponent(conversationId)}/`,
		{ signal },
	)

	const content = typeof data?.content === 'string' ? data.content : ''

	if (!content) {
		return []
	}

	return content
		.split('<br>')
		.map((entry) => stripHtml(decodeHtmlEntities(entry)).trim())
		.filter(Boolean)
		.filter((entry) => !entry.toLowerCase().startsWith('download completo'))
		.map((entry, index) => {
			const matched = entry.match(/^\[(.*?)\]\s*(.*?):\s*(.*)$/)

			if (!matched) {
				return {
					id: `fallback-${index}`,
					message: entry,
					pushName: '',
					timestamp: '',
					iso_time: '',
					fromMe: false,
				}
			}

			const [, timestampLabel, pushName, message] = matched
			const isoTime = timestampLabel.match(/(\d{2}:\d{2})$/)?.[1] || ''

			return {
				id: `fallback-${index}`,
				message,
				pushName,
				timestamp: timestampLabel,
				iso_time: isoTime,
				fromMe: false,
			}
		})
}

export const getPreferredAccount = (accounts = []) => {
	if (!accounts.length) {
		return null
	}

	return accounts.find((account) => isScoutAccountActive(account)) || accounts[0]
}

export const isGroupParticipantsUpdateMessage = (message) => {
	return message?.simplified_type === 'group_participants_update' || message?.messageType === 'groupParticipantsUpdate'
}

const normalizeParticipantPhone = (value = '') => {
	const normalizedValue = normalizeParticipantValue(value)
	const normalizedLocalPart = normalizedValue
		.trim()
		.split('@')[0]
		.replace(/:\d+$/, '')

	return normalizedLocalPart.replace(/\D/g, '')
}

const normalizeParticipantValue = (value = '') => {
	if (typeof value === 'string' || typeof value === 'number') {
		const normalized = String(value).trim()
		if (!normalized || normalized.toLowerCase().includes('[object object]')) {
			return ''
		}

		return normalized
	}

	return ''
}

const normalizeParticipantJid = (value = '') => {
	const normalized = normalizeParticipantValue(value).toLowerCase()
		.replace(/^([^@]+):\d+(@.*)$/, '$1$2')
	return normalized.includes('@') ? normalized : ''
}

const normalizeParticipantLid = (value = '') => {
	const normalized = normalizeParticipantJid(value)
	return normalized.includes('@lid') ? normalized : ''
}

const normalizeGroupEventAction = (value = '') => {
	const normalized = normalizeParticipantValue(value).toLowerCase()

	switch (normalized) {
		case 'add':
		case 'added':
		case 'participant_added':
			return 'added'
		case 'left':
		case 'leave':
		case 'participant_left':
			return 'left'
		case 'remove':
		case 'removed':
		case 'participant_removed':
			return 'removed'
		case 'promote':
		case 'promoted':
			return 'promote'
		case 'demote':
		case 'demoted':
			return 'demote'
		default:
			return ''
	}
}

const normalizeArrayInput = (value) => {
	if (Array.isArray(value)) {
		return value
	}

	if (value === null || value === undefined) {
		return []
	}

	return [value]
}

const getScalarParticipantValue = (...candidates) => {
	for (const candidate of candidates) {
		const normalized = normalizeParticipantValue(candidate)
		if (normalized) {
			return normalized
		}
	}

	return ''
}

const extractParticipantIdFromValue = (value) => {
	if (value === null || value === undefined) {
		return ''
	}

	if (typeof value === 'string' || typeof value === 'number') {
		return normalizeParticipantValue(value)
	}

	if (typeof value !== 'object') {
		return ''
	}

	const directIdentifier = getScalarParticipantValue(
		value?.participant
		|| value?.participant_original
		|| value?.participant_jid
		|| value?.participant_lid
		|| value?.jid
		|| value?.id
		|| value?.lid
		|| value?.phoneNumber
		|| value?.phone_number
		|| value?.participant_phone,
	)
	if (directIdentifier) {
		return directIdentifier
	}

	return getScalarParticipantValue(
		extractParticipantIdFromValue(value?.participant),
		extractParticipantIdFromValue(value?.user),
		extractParticipantIdFromValue(value?.member),
		extractParticipantIdFromValue(value?.target),
		extractParticipantIdFromValue(value?.actor),
		extractParticipantIdFromValue(value?.author),
	)
}

const extractParticipantsData = (payload) => {
	const candidates = [
		payload?.participantsData,
		payload?.participants_data,
		payload?.participantData,
		payload?.participant_data,
		payload?.members,
		payload?.member,
		payload?.users,
		payload?.user,
		payload?.recipient,
		payload?.recipients,
	]

	const normalizedEntries = []

	candidates.forEach((candidate) => {
		normalizeArrayInput(candidate).forEach((entry) => {
			if (entry && typeof entry === 'object') {
				normalizedEntries.push(entry)
				return
			}

			const candidateId = extractParticipantIdFromValue(entry)
			if (candidateId) {
				normalizedEntries.push({ jid: candidateId, id: candidateId })
			}
		})
	})

	return normalizedEntries
}

const extractParticipantIds = (payload) => {
	const candidates = [
		payload?.participants,
		payload?.members,
		payload?.member,
		payload?.users,
		payload?.user,
		payload?.recipient,
		payload?.recipients,
		payload?.target,
		payload?.targets,
	]
	const ids = []

	candidates.forEach((candidate) => {
		normalizeArrayInput(candidate).forEach((entry) => {
			const participantId = extractParticipantIdFromValue(entry)
			if (participantId) {
				ids.push(participantId)
			}
		})
	})

	return ids
}

const getParticipantNameCandidate = (participant) => {
	if (!participant || typeof participant !== 'object') {
		return ''
	}

	const candidates = [
		participant?.participant_display_name,
		participant?.participantDisplayName,
		participant?.display_name,
		participant?.displayName,
		participant?.contact_name,
		participant?.contactName,
		participant?.participant_name,
		participant?.participantName,
		participant?.name,
		participant?.push_name,
		participant?.pushName,
	]

	for (const candidate of candidates) {
		const normalizedCandidate = normalizeParticipantValue(candidate)
		if (normalizedCandidate) {
			return normalizedCandidate
		}
	}

	return ''
}

const resolveParticipantIdentity = (participant, options = {}) => {
	const participantObject = participant && typeof participant === 'object' ? participant : {}
	const original = normalizeParticipantValue(
		participantObject?.participant_original
		|| participantObject?.participantOriginal
		|| participantObject?.id
		|| participantObject?.jid
		|| participantObject?.lid
		|| participantObject?.participant
	)
	const lid = normalizeParticipantLid(participantObject?.participant_lid || participantObject?.participantLid || participantObject?.lid || participantObject?.jid || original)
	const jid = normalizeParticipantJid(participantObject?.participant_jid || participantObject?.participantJid || participantObject?.jid || participantObject?.id || original)
	const directPhone = normalizeParticipantPhone(
		participantObject?.participant_phone
		|| participantObject?.participantPhone
		|| participantObject?.phone_number
		|| participantObject?.phoneNumber
		|| participantObject?.pn
		|| participantObject?.userPn
		|| participantObject?.authorPn,
	)
	const phone = normalizeParticipantPhone(
		directPhone
		|| jid
		|| original,
	)
	const hasStrongIdentifier = Boolean(original || lid || jid || directPhone || phone)
	const name = hasStrongIdentifier ? getParticipantNameCandidate(participantObject) : ''
	const connectedPhone = normalizeParticipantPhone(options?.connectedPhone)
	const connectedJid = normalizeParticipantJid(options?.connectedJid || '')
	const connectedLid = normalizeParticipantLid(options?.connectedLid || '')
	const explicitCurrentInstance = participantObject?.participant_is_current_instance ?? participantObject?.participantIsCurrentInstance
	const isCurrentInstance = explicitCurrentInstance === true
		|| (phone && connectedPhone && phone === connectedPhone)
		|| (jid && connectedJid && jid === connectedJid)
		|| (lid && connectedLid && lid === connectedLid)

	return {
		original,
		lid,
		jid,
		phone,
		name,
		isCurrentInstance,
	}
}

const getParticipantDisplayName = (identity, participantDirectory) => {
	if (!identity) {
		return ''
	}

	if (identity.isCurrentInstance) {
		return 'Você'
	}

	if (identity.name) {
		return identity.name
	}

	if (participantDirectory instanceof Map) {
		const directoryCandidates = [identity.phone, identity.jid, identity.lid, identity.original]
			.map((candidate) => String(candidate || '').trim())
			.filter(Boolean)
		for (const candidate of directoryCandidates) {
			const directLabel = String(participantDirectory.get(candidate) || '').trim()
			if (directLabel) {
				return directLabel
			}
		}
	}

	if (identity.phone) {
		return formatPhoneNumber(identity.phone)
	}

	if (identity.jid) {
		return identity.jid
	}

	if (identity.lid) {
		return identity.lid
	}

	if (identity.original) {
		return identity.original
	}

	return ''
}

const joinParticipantNames = (names = []) => {
	const normalizedNames = names.filter(Boolean)

	if (!normalizedNames.length) {
		return 'Participante'
	}

	if (normalizedNames.length === 1) {
		return normalizedNames[0]
	}

	if (normalizedNames.length === 2) {
		return `${normalizedNames[0]} e ${normalizedNames[1]}`
	}

	return `${normalizedNames.slice(0, -1).join(', ')} e ${normalizedNames[normalizedNames.length - 1]}`
}

export const formatGroupParticipantsUpdateText = (message, options = {}) => {
	const payload = message?.message && typeof message.message === 'object' ? message.message : {}
	const mergedPayload = {
		...(message && typeof message === 'object' ? message : {}),
		...payload,
	}
	const action = normalizeGroupEventAction(
		mergedPayload?.group_event_action
		|| mergedPayload?.groupEventAction
		|| mergedPayload?.action,
	)
	const participantsData = extractParticipantsData(mergedPayload)
	const participantIds = extractParticipantIds(mergedPayload)
	const participantSingleIdentity = mergedPayload?.participant || mergedPayload?.participant_original || mergedPayload?.participant_lid || mergedPayload?.participant_jid || mergedPayload?.participant_phone || mergedPayload?.participant_name || mergedPayload?.participant_display_name
		? [
			{
				id: mergedPayload?.participant || mergedPayload?.participant_original || mergedPayload?.participant_jid || mergedPayload?.participant_lid || mergedPayload?.participant_phone || '',
				participant_original: mergedPayload?.participant_original,
				participant_lid: mergedPayload?.participant_lid,
				participant_jid: mergedPayload?.participant_jid,
				participant_phone: mergedPayload?.participant_phone,
				participant_name: mergedPayload?.participant_name,
				participant_display_name: mergedPayload?.participant_display_name,
				participant_is_current_instance: mergedPayload?.participant_is_current_instance,
			},
		]
		: []
	const participantsSource = participantsData.length
		? participantsData
		: participantIds.length
			? participantIds.map((participantId) => ({ jid: participantId, id: participantId }))
			: participantSingleIdentity

	const participants = participantsSource
		.map((participant) => {
			const identity = resolveParticipantIdentity(participant, options)
			const label = getParticipantDisplayName(identity, options?.participantDirectory)

			return {
				id: identity.phone || identity.jid || identity.lid || identity.original,
				phone: identity.phone,
				jid: identity.jid,
				lid: identity.lid,
				original: identity.original,
				label,
				isCurrentInstance: identity.isCurrentInstance,
			}
		})
		.filter((participant) => participant.label)

	const authorIdentity = resolveParticipantIdentity({
		id: mergedPayload?.author,
		jid: mergedPayload?.author,
		participant_phone: mergedPayload?.authorPn || mergedPayload?.author_pn,
		participant_lid: mergedPayload?.authorLid || mergedPayload?.author_lid,
		participant_name: mergedPayload?.authorData?.name || mergedPayload?.author_data?.name || mergedPayload?.pushName || mergedPayload?.push_name || mergedPayload?.sender,
		participant_display_name: mergedPayload?.authorData?.name || mergedPayload?.author_data?.name || mergedPayload?.pushName || mergedPayload?.push_name || mergedPayload?.sender,
		pushName: mergedPayload?.authorData?.name || mergedPayload?.author_data?.name || mergedPayload?.pushName || mergedPayload?.push_name || mergedPayload?.sender,
	}, options)
	const authorLabel = getParticipantDisplayName(authorIdentity, options?.participantDirectory)
	const authorKeys = new Set(
		[
			authorIdentity.phone,
			authorIdentity.jid,
			authorIdentity.lid,
			authorIdentity.original,
		].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean),
	)
	const normalizedAuthorLabel = String(authorLabel || '').trim().toLowerCase()
	const participantsWithoutAuthor = action === 'removed' && participants.length > 1
		? participants.filter((participant) => {
			const participantKeys = [
				participant.id,
				participant.phone,
				participant.jid,
				participant.lid,
				participant.original,
			].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
			const matchesAuthorById = participantKeys.some((key) => authorKeys.has(key))
			if (matchesAuthorById) {
				return false
			}

			// If the event has no stable id for one participant, fallback by label only in this weak-id scenario.
			const hasStrongIdentifier = participantKeys.length > 0
			const participantLabelNormalized = String(participant.label || '').trim().toLowerCase()
			if (!hasStrongIdentifier && normalizedAuthorLabel && participantLabelNormalized === normalizedAuthorLabel) {
				return false
			}

			return true
		})
		: participants
	const effectiveParticipants = participantsWithoutAuthor.length ? participantsWithoutAuthor : participants
	let participantLabel = joinParticipantNames(effectiveParticipants.map((participant) => participant.label))
	const actorMatchesParticipants = authorKeys.size > 0
		&& effectiveParticipants.length > 0
		&& effectiveParticipants.every((participant) => {
			const participantKeys = [
				participant.id,
				participant.phone,
				participant.jid,
				participant.lid,
				participant.original,
			].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
			return participantKeys.some((key) => authorKeys.has(key))
		})
	if (actorMatchesParticipants && authorIdentity.isCurrentInstance) {
		participantLabel = 'Você'
	} else if (actorMatchesParticipants && authorLabel) {
		participantLabel = authorLabel
	}
	const resolvedAction = action === 'removed' && actorMatchesParticipants ? 'left' : action
	const connectedNameNormalized = normalizeParticipantValue(options?.connectedName).toLowerCase()
	if (resolvedAction === 'left' && participantLabel !== 'Você' && connectedNameNormalized) {
		const participantLabelsNormalized = effectiveParticipants
			.map((participant) => String(participant.label || '').trim().toLowerCase())
			.filter(Boolean)
		const authorLabelNormalized = String(authorLabel || '').trim().toLowerCase()
		const participantsMatchConnectedName = participantLabelsNormalized.length > 0
			&& participantLabelsNormalized.every((label) => label === connectedNameNormalized)
		const authorMatchesConnectedName = authorLabelNormalized === connectedNameNormalized

		if (participantsMatchConnectedName || (actorMatchesParticipants && authorMatchesConnectedName)) {
			participantLabel = 'Você'
		}
	}

	switch (resolvedAction) {
		case 'added':
			return `${participantLabel} ${effectiveParticipants.length > 1 ? 'foram adicionados' : 'foi adicionado'} ao grupo`
		case 'left':
			return `${participantLabel} ${effectiveParticipants.length > 1 ? 'saíram' : 'saiu'} do grupo`
		case 'removed':
			if (authorLabel && !actorMatchesParticipants) {
				return `${authorLabel} removeu ${participantLabel}`
			}
			return `${participantLabel} ${effectiveParticipants.length > 1 ? 'foram removidos' : 'foi removido'} do grupo`
		case 'promote':
			return `${participantLabel} ${effectiveParticipants.length > 1 ? 'viraram administradores' : 'virou administrador'}`
		case 'demote':
			return `${participantLabel} ${effectiveParticipants.length > 1 ? 'deixaram de ser administradores' : 'deixou de ser administrador'}`
		default:
			return 'Atualização de participantes do grupo'
	}
}

export const getInitials = (value = '') => {
	const words = String(value)
		.trim()
		.split(/\s+/)
		.filter(Boolean)

	if (!words.length) {
		return 'KS'
	}

	return words
		.slice(0, 2)
		.map((entry) => entry[0]?.toUpperCase() || '')
		.join('')
}

export const formatPhoneNumber = (value = '') => {
	const digits = String(value).replace(/\D/g, '')

	if (digits.length === 13) {
		return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`
	}

	if (digits.length === 12) {
		return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`
	}

	if (digits.length === 11) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
	}

	return value || 'Sem numero'
}

export const formatDateLabel = (value) => {
	if (!value) {
		return ''
	}

	return dayjs(value).format('DD/MM/YYYY')
}

export const formatTimestampLabel = (value) => {
	if (!value) {
		return ''
	}

	if (typeof value === 'string' && /\d{2}:\d{2}$/.test(value)) {
		return value.match(/(\d{2}:\d{2})$/)?.[1] || value
	}

	const numericValue = Number(value)
	const stamp = numericValue > 9999999999 ? dayjs(numericValue) : dayjs.unix(numericValue)

	return stamp.isValid() ? stamp.format('HH:mm') : ''
}

export const formatConversationDate = (value) => {
	if (!value) {
		return ''
	}

	if (typeof value === 'string' && /\d{2}:\d{2}$/.test(value)) {
		return value.match(/(\d{2}:\d{2})$/)?.[1] || value
	}

	const numericValue = Number(value)
	const stamp = numericValue > 9999999999 ? dayjs(numericValue) : dayjs.unix(numericValue)

	if (!stamp.isValid()) {
		return ''
	}

	const today = dayjs().startOf('day')
	const yesterday = today.subtract(1, 'day')

	if (!stamp.isBefore(today)) {
		return stamp.format('HH:mm')
	}

	if (!stamp.isBefore(yesterday)) {
		return 'Ontem'
	}

	return stamp.format('DD/MM/YY')
}

const normalizeEventTimestamp = (value) => {
	if (value === null || value === undefined) {
		return null
	}

	if (typeof value === 'number' && Number.isFinite(value)) {
		return Math.trunc(value)
	}

	if (typeof value === 'string' && value.trim() !== '') {
		const numericValue = Number(value)
		return Number.isFinite(numericValue) ? Math.trunc(numericValue) : null
	}

	if (typeof value === 'object') {
		const low = Number(value.low || 0)
		const high = Number(value.high || 0)

		if (Number.isFinite(low) && Number.isFinite(high)) {
			return Math.trunc(high) * (2 ** 32) + Math.trunc(low)
		}
	}

	return null
}

export const formatEventDateTime = (value) => {
	const normalizedTimestamp = normalizeEventTimestamp(value)
	if (!normalizedTimestamp) {
		return ''
	}

	const stamp = dayjs.unix(normalizedTimestamp)
	return stamp.isValid() ? stamp.format('DD/MM/YYYY HH:mm') : ''
}

const getStructuredMessagePayload = (payload, key) => {
	if (!payload || typeof payload !== 'object') {
		return null
	}

	if (payload[key] && typeof payload[key] === 'object') {
		return payload[key]
	}

	if (payload.message && typeof payload.message === 'object' && payload.message[key] && typeof payload.message[key] === 'object') {
		return payload.message[key]
	}

	return payload
}

const parseVcardFieldValues = (vcard, prefixes) => {
	if (!vcard || typeof vcard !== 'string') {
		return []
	}

	const prefixList = Array.isArray(prefixes) ? prefixes : [prefixes]
	const normalizedPrefixes = prefixList.map((value) => String(value || '').trim().toUpperCase()).filter(Boolean)

	return String(vcard)
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter((line) => {
			const upperLine = line.toUpperCase()
			return normalizedPrefixes.some((prefix) => upperLine.startsWith(prefix))
		})
		.map((line) => line.split(':').slice(1).join(':').trim())
		.filter(Boolean)
}

const formatLocationCoordinates = (latitude, longitude) => {
	const lat = Number(latitude)
	const lng = Number(longitude)

	if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
		return ''
	}

	return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
}

const buildGoogleMapsUrl = (latitude, longitude) => {
	const lat = Number(latitude)
	const lng = Number(longitude)

	if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
		return ''
	}

	return `https://maps.google.com/?q=${lat},${lng}`
}

export const getLocationMessageDetails = (locationPayload) => {
	const normalizedPayload = getStructuredMessagePayload(locationPayload, 'locationMessage')
	if (!normalizedPayload || typeof normalizedPayload !== 'object') {
		return null
	}

	const title = typeof normalizedPayload.name === 'string' && normalizedPayload.name.trim()
		? normalizedPayload.name.trim()
		: 'Localização compartilhada'
	const address = typeof normalizedPayload.address === 'string' ? normalizedPayload.address.trim() : ''
	const coordinatesLabel = formatLocationCoordinates(normalizedPayload.latitude ?? normalizedPayload.degreesLatitude, normalizedPayload.longitude ?? normalizedPayload.degreesLongitude)
	const mapUrl = typeof normalizedPayload.mapUrl === 'string' && normalizedPayload.mapUrl.trim()
		? normalizedPayload.mapUrl.trim()
		: buildGoogleMapsUrl(normalizedPayload.latitude ?? normalizedPayload.degreesLatitude, normalizedPayload.longitude ?? normalizedPayload.degreesLongitude)
	const thumbnail = typeof normalizedPayload.thumbnail === 'string' && normalizedPayload.thumbnail.trim()
		? normalizedPayload.thumbnail.trim()
		: typeof normalizedPayload.jpegThumbnail === 'string' && normalizedPayload.jpegThumbnail.trim()
			? normalizedPayload.jpegThumbnail.trim()
			: ''
	const previewLabel = title !== 'Localização compartilhada'
		? `Localização: ${title}`
		: address
			? `Localização: ${address}`
			: coordinatesLabel
				? `Localização: ${coordinatesLabel}`
				: 'Localização compartilhada'

	return {
		title,
		address,
		coordinatesLabel,
		mapUrl,
		thumbnail,
		previewLabel,
	}
}

export const getContactMessageDetails = (contactPayload) => {
	const normalizedPayload = getStructuredMessagePayload(contactPayload, 'contactMessage')
	if (!normalizedPayload || typeof normalizedPayload !== 'object') {
		return null
	}

	const phones = Array.isArray(normalizedPayload.phones) && normalizedPayload.phones.length
		? normalizedPayload.phones.filter(Boolean)
		: parseVcardFieldValues(normalizedPayload.vcard, ['TEL', 'ITEM1.TEL', 'ITEM2.TEL', 'ITEM3.TEL', 'ITEM4.TEL'])
	const emails = Array.isArray(normalizedPayload.emails) && normalizedPayload.emails.length
		? normalizedPayload.emails.filter(Boolean)
		: parseVcardFieldValues(normalizedPayload.vcard, ['EMAIL', 'ITEM1.EMAIL', 'ITEM2.EMAIL'])
	const businessName = typeof normalizedPayload.businessName === 'string' ? normalizedPayload.businessName.trim() : ''
	const businessDescription = typeof normalizedPayload.businessDescription === 'string' ? normalizedPayload.businessDescription.trim() : ''
	const title = typeof normalizedPayload.fullName === 'string' && normalizedPayload.fullName.trim()
		? normalizedPayload.fullName.trim()
		: typeof normalizedPayload.displayName === 'string' && normalizedPayload.displayName.trim()
			? normalizedPayload.displayName.trim()
			: businessName || 'Contato compartilhado'
	const subtitle = businessName && businessName !== title ? businessName : ''
	const primaryPhone = typeof normalizedPayload.phone === 'string' && normalizedPayload.phone.trim()
		? normalizedPayload.phone.trim()
		: phones[0] || ''
	const primaryEmail = typeof normalizedPayload.email === 'string' && normalizedPayload.email.trim()
		? normalizedPayload.email.trim()
		: emails[0] || ''
	const address = typeof normalizedPayload.address === 'string' ? normalizedPayload.address.trim() : ''
	const previewLabel = title !== 'Contato compartilhado' ? `Contato: ${title}` : primaryPhone ? `Contato: ${primaryPhone}` : 'Contato compartilhado'

	return {
		title,
		subtitle,
		phones,
		emails,
		primaryPhone,
		primaryEmail,
		address,
		businessName,
		businessDescription,
		previewLabel,
	}
}

export const getEventMessageDetails = (eventPayload) => {
	const normalizedPayload = getStructuredMessagePayload(eventPayload, 'eventMessage')
	if (!normalizedPayload || typeof normalizedPayload !== 'object') {
		return null
	}

	const location = normalizedPayload.location && typeof normalizedPayload.location === 'object'
		? normalizedPayload.location
		: {}

	const title = typeof normalizedPayload.name === 'string' && normalizedPayload.name.trim()
		? normalizedPayload.name.trim()
		: 'Evento'
	const description = typeof normalizedPayload.description === 'string' ? normalizedPayload.description.trim() : ''
	const startLabel = formatEventDateTime(normalizedPayload.startTime)
	const endLabel = formatEventDateTime(normalizedPayload.endTime)
	const joinLink = typeof normalizedPayload.joinLink === 'string' ? normalizedPayload.joinLink.trim() : ''
	const locationName = typeof location.name === 'string' && location.name.trim()
		? location.name.trim()
		: typeof normalizedPayload.location_name === 'string' && normalizedPayload.location_name.trim()
			? normalizedPayload.location_name.trim()
			: ''
	const locationAddress = typeof location.address === 'string' && location.address.trim()
		? location.address.trim()
		: typeof normalizedPayload.location_address === 'string' && normalizedPayload.location_address.trim()
			? normalizedPayload.location_address.trim()
			: ''
	const locationLabel = locationName || locationAddress || ''
	const statusLabel = normalizedPayload.isCanceled ? 'Evento cancelado' : normalizedPayload.isScheduleCall ? 'Chamada agendada' : 'Evento'

	return {
		title,
		description,
		startLabel,
		endLabel,
		joinLink,
		locationLabel,
		statusLabel,
		isCanceled: Boolean(normalizedPayload.isCanceled),
		isScheduleCall: Boolean(normalizedPayload.isScheduleCall),
		extraGuestsAllowed: Boolean(normalizedPayload.extraGuestsAllowed),
		eventResponses: Array.isArray(normalizedPayload.eventResponses)
			? normalizedPayload.eventResponses
			: Array.isArray(eventPayload?.eventResponses)
				? eventPayload.eventResponses
				: [],
	}
}

const EVENT_RESPONSE_LABELS = {
	1: 'Vou',
	2: 'Não',
	3: 'Talvez',
	'1': 'Vou',
	'2': 'Não',
	'3': 'Talvez',
	GOING: 'Vou',
	NOT_GOING: 'Não',
	MAYBE: 'Talvez',
}

const getEventResponsePayload = (eventResponsePayload) => {
	if (!eventResponsePayload || typeof eventResponsePayload !== 'object') {
		return null
	}

	if (eventResponsePayload.eventResponseMessage && typeof eventResponsePayload.eventResponseMessage === 'object') {
		return eventResponsePayload.eventResponseMessage
	}

	if (eventResponsePayload.event_response_details && typeof eventResponsePayload.event_response_details === 'object') {
		return eventResponsePayload.event_response_details
	}

	if (eventResponsePayload.message?.eventResponseMessage && typeof eventResponsePayload.message.eventResponseMessage === 'object') {
		return eventResponsePayload.message.eventResponseMessage
	}

	if (eventResponsePayload.message?.encEventResponseMessage?.eventResponseMessage && typeof eventResponsePayload.message.encEventResponseMessage.eventResponseMessage === 'object') {
		return eventResponsePayload.message.encEventResponseMessage.eventResponseMessage
	}

	if (eventResponsePayload.response || eventResponsePayload.responseLabel) {
		return eventResponsePayload
	}

	return null
}

const getEventResponseLabel = (response) => {
	if (response && typeof response === 'object') {
		return getEventResponseLabel(response.response)
	}

	if (typeof response === 'string') {
		const normalizedResponse = response.trim()
		return EVENT_RESPONSE_LABELS[normalizedResponse] || EVENT_RESPONSE_LABELS[normalizedResponse.toUpperCase()] || ''
	}

	return EVENT_RESPONSE_LABELS[response] || ''
}

const getEventResponseValue = (payload) => {
	if (!payload || typeof payload !== 'object') {
		return payload
	}

	if (payload.response && typeof payload.response === 'object') {
		return payload.response.response
	}

	return payload.response
}

export const getEventResponseMessageDetails = (eventResponsePayload) => {
	const normalizedPayload = getEventResponsePayload(eventResponsePayload)
	const rawText = typeof eventResponsePayload?.message === 'string' && eventResponsePayload.message.trim()
		? eventResponsePayload.message.trim()
		: ''
	const response = getEventResponseValue(normalizedPayload)
	const label = normalizedPayload?.responseLabel || getEventResponseLabel(response)

	return {
		response,
		responseLabel: label || rawText || 'Evento',
		previewLabel: rawText || (label ? label : 'Evento'),
	}
}

const normalizePollOptionName = (option) => {
	if (typeof option === 'string') {
		return option.trim()
	}

	if (!option || typeof option !== 'object') {
		return ''
	}

	if (typeof option.optionName === 'string' && option.optionName.trim()) {
		return option.optionName.trim()
	}

	if (typeof option.name === 'string' && option.name.trim()) {
		return option.name.trim()
	}

	if (typeof option.text === 'string' && option.text.trim()) {
		return option.text.trim()
	}

	return ''
}

const normalizePollVoteCount = (update) => {
	if (!update || typeof update !== 'object') {
		return 0
	}

	const directVoteCount = Number(update.voteCount)
	if (Number.isFinite(directVoteCount)) {
		return directVoteCount
	}

	if (Array.isArray(update.voters)) {
		return update.voters.length
	}

	return 0
}

export const getPollMessageDetails = (pollPayload) => {
	const normalizedPayload = getStructuredMessagePayload(pollPayload, 'pollCreationMessageV3')
	if (!normalizedPayload || typeof normalizedPayload !== 'object') {
		return null
	}

	const title = typeof normalizedPayload.name === 'string' && normalizedPayload.name.trim()
		? normalizedPayload.name.trim()
		: typeof normalizedPayload.title === 'string' && normalizedPayload.title.trim()
			? normalizedPayload.title.trim()
			: 'Enquete'
	const selectableOptionsCount = Number(
		normalizedPayload.selectableOptionsCount ?? normalizedPayload.selectableCount ?? 0
	)
	const optionsSource = Array.isArray(normalizedPayload.options) ? normalizedPayload.options : []
	const updatesSource = Array.isArray(normalizedPayload.pollUpdates)
		? normalizedPayload.pollUpdates
		: Array.isArray(pollPayload?.pollUpdates)
			? pollPayload.pollUpdates
			: []
	const voteMap = new Map(
		updatesSource
			.map((update) => {
				const optionName = normalizePollOptionName(update)
				return [optionName, normalizePollVoteCount(update)]
			})
			.filter(([optionName]) => Boolean(optionName))
	)
	const votersMap = new Map(
		updatesSource
			.map((update) => {
				const optionName = normalizePollOptionName(update)
				const voters = Array.isArray(update?.voters) ? update.voters.filter(Boolean) : []
				return [optionName, voters]
			})
			.filter(([optionName]) => Boolean(optionName))
	)
	const options = optionsSource
		.map((option, index) => {
			const optionName = normalizePollOptionName(option)
			return {
				id: optionName || `option-${index}`,
				name: optionName || `Opção ${index + 1}`,
				voteCount: voteMap.get(optionName) ?? 0,
				voters: votersMap.get(optionName) ?? [],
			}
		})
		.filter((option) => Boolean(option.name))
	const totalVotes = options.reduce((sum, option) => sum + Number(option.voteCount || 0), 0)
	const previewLabel = title ? `Enquete: ${title}` : 'Enquete'

	return {
		title,
		options,
		totalVotes,
		selectableOptionsCount: Number.isFinite(selectableOptionsCount) ? selectableOptionsCount : 0,
		previewLabel,
	}
}

export const formatConversationPreview = (conversation, options = {}) => {
	const type = conversation?.message_type
	const message = conversation?.message || {}
	const editedText = getProtocolEditedMessageText(message) || getTextFromMessagePayload(message)

	if (message?.type === 'DELETED')
		return 'Mensagem apagada'

	if (message?.type === 'EDITED')
		return editedText || 'Mensagem editada'

	if (isEditedProtocolMessagePayload(message))
		return editedText || 'Mensagem editada'

	if (isDeletedProtocolMessagePayload(message))
		return 'Mensagem apagada'

	if (type === 'groupParticipantsUpdate' || isGroupParticipantsUpdateMessage(conversation?.message)) {
		return formatGroupParticipantsUpdateText({
			messageType: type,
			simplified_type: conversation?.message?.simplified_type,
			message,
		}, options)
	}

	if (typeof message === 'string')
		return message

	if (message.reaction)
		return `Reagiu com ${message.reaction}`

	if (message.conversation)
		return message.conversation

	if (message.extendedTextMessage?.text)
		return message.extendedTextMessage.text

	if (message.imageMessage?.caption)
		return message.imageMessage.caption

	if (message.videoMessage?.caption)
		return message.videoMessage.caption

	if (message.documentMessage?.fileName)
		return `Documento: ${message.documentMessage.fileName}`
	
	if (message.pollCreationMessageV3) {
		const details = getPollMessageDetails(message)
		return details?.previewLabel || 'Enquete'
	}

	if (
		type === 'eventResponseMessage'
		|| type === 'encEventResponseMessage'
		|| message?.messageType === 'eventResponseMessage'
		|| message?.messageType === 'encEventResponseMessage'
		|| message?.eventResponseMessage
		|| message?.event_response_details
	) {
		return 'Evento'
	}

	switch (type) {
		case "audioMessage": 
			return "Áudio"
		case "stickerMessage": 
			return "Sticker"
		case "locationMessage":
		case "liveLocationMessage":
			return "Localização"
		case "imageMessage": 
			return "Foto"
		case "videoMessage": 
			return "Vídeo"
		case "contactMessage": 
			return "Contato"
		case "eventMessage": 
			return "Evento"
		case "eventResponseMessage":
		case "encEventResponseMessage":
			return "Evento"
		case "pollCreationMessageV3":
		case "pollUpdateMessage":
			return "Enquete"
		case "editedMessage":
			return editedText || "Mensagem editada"
		case "protocolMessage":
			return editedText || "Mensagem não suportada"
		case "templateMessage":
			const templateMessage = message?.templateMessage
			if (!templateMessage)
				return "Mensagem não suportada"
			if ("interactiveMessageTemplate" in templateMessage)
				return templateMessage.interactiveMessageTemplate.body?.text || "Mensagem não suportada"
			if ("hydratedTemplate" in templateMessage)
				return templateMessage.hydratedTemplate.hydratedContentText || "Mensagem não suportada"
			return "Mensagem não suportada"
		case "reactionMessage":
			const text = message?.reactionMessage?.text
			if (text)
				return `Reagiu com ${text}`
			return "Mensagem não suportada"
		case "documentMessage":
			const fileName = message?.title
			if (fileName)
				return `Documento: ${fileName}`
			return "Documento"

		default:
			return "Mensagem não suportada"
	}
}

export const formatKwikMessageContent = (message) => {
	if (!message) {
		return 'Mensagem indisponível'
	}

	if (isGroupParticipantsUpdateMessage(message)) {
		return formatGroupParticipantsUpdateText(message)
	}

	const protocolEditedText = getProtocolEditedMessageText(message)
	if (isEditedProtocolMessagePayload(message) && protocolEditedText) {
		return protocolEditedText
	}

	if (message.type === 'DELETED') {
		return 'Mensagem apagada'
	}

	if (message.type === 'EDITED' && typeof message.message === 'string' && message.message.trim()) {
		return message.message
	}

	if (typeof message.message === 'string' && message.message.trim()) {
		return message.message
	}

	if (message.caption) {
		return message.caption
	}

	if (message.title) {
		return message.title
	}

	if (message.simplified_type === 'location' || message.messageType === 'locationMessage') {
		const details = getLocationMessageDetails(message)
		return details?.previewLabel || 'Localização compartilhada'
	}

	if (message.simplified_type === 'contact' || message.messageType === 'contactMessage') {
		const details = getContactMessageDetails(message)
		return details?.previewLabel || 'Contato compartilhado'
	}

	if (message.simplified_type === 'event' || message.messageType === 'eventMessage') {
		const details = getEventMessageDetails(message)
		return details?.title ? `Evento: ${details.title}` : 'Evento'
	}

	if (message.simplified_type === 'event_response' || message.messageType === 'eventResponseMessage' || message.messageType === 'encEventResponseMessage') {
		return 'Evento'
	}

	if (message.simplified_type === 'poll_creaction_message_v3' || message.messageType === 'pollCreationMessageV3') {
		const details = getPollMessageDetails(message)
		return details?.previewLabel || 'Enquete'
	}

	if (message.simplified_type === 'media') {
		return 'Midia enviada'
	}

	if (message.transcription_data?.text) {
		return message.transcription_data.text
	}

	return 'Conteúdo não suportado'
}

const appendSearchText = (parts, value) => {
	if (typeof value === 'string' && value.trim()) {
		parts.push(value.trim())
	}
}

export const getKwikMessageSearchText = (message) => {
	if (!message) {
		return ''
	}

	const parts = []
	appendSearchText(parts, formatKwikMessageContent(message))
	appendSearchText(parts, message.message)
	appendSearchText(parts, getTextFromMessagePayload(message.message))
	appendSearchText(parts, getTextFromMessagePayload(message))
	appendSearchText(parts, message.caption)
	appendSearchText(parts, message.title)
	appendSearchText(parts, getProtocolEditedMessageText(message))
	appendSearchText(parts, message.pushName)
	appendSearchText(parts, message.sender)

	return Array.from(new Set(parts)).join(' ')
}

export const formatMessageContent = (message) => {
	if (!message) {
		return 'Mensagem indisponível'
	}

	if (isGroupParticipantsUpdateMessage(message)) {
		return formatGroupParticipantsUpdateText(message)
	}

	if (typeof message.message === 'string' && message.message.trim()) {
		return message.message
	}

	if (message.messageType === 'reactionMessage') {
		return `Reagiu com ${message.message?.reaction || 'uma reação'}`
	}

	if (message.simplified_type === 'media') {
		return message.caption || message.title || 'Midia enviada'
	}

	if (message.simplified_type === 'contact' || message.messageType === 'contactMessage') {
		const details = getContactMessageDetails(message)
		return details?.previewLabel || 'Contato compartilhado'
	}

	if (message.simplified_type === 'location' || message.messageType === 'locationMessage') {
		const details = getLocationMessageDetails(message)
		return details?.previewLabel || 'Localização compartilhada'
	}

	if (message.simplified_type === 'poll_creaction_message_v3' || message.messageType === 'pollCreationMessageV3') {
		const details = getPollMessageDetails(message)
		return details?.previewLabel || 'Enquete'
	}

	if (message.simplified_type === 'event_response' || message.messageType === 'eventResponseMessage' || message.messageType === 'encEventResponseMessage') {
		return 'Evento'
	}

	if (message.type === 'DELETED') {
		return 'Mensagem apagada'
	}

	if (message.type === 'EDITED') {
		return message.message || 'Mensagem editada'
	}

	return 'Conteúdo não suportado'
}

export const getContactData = async (enterpriseId, phoneNumber) => {
	if (!enterpriseId || !phoneNumber)
		throw new Error("[getContactData] Missing enterpriseId or phoneNumber.")
	
	loader.on()
	try {
		return await fetchKwik(`/enterprises/${enterpriseId}/contacts/retrieve/WHATSAPP@@@+${phoneNumber}/`)
	} catch (err) {
		if (err.status === 404) {
			return null
		}
		throw err
	} finally {
		loader.off()
	}
}

export const getContactEnterprises = async (enterpriseId) => {
	if (!enterpriseId)
		throw new Error("[getContactEnterprises] Missing enterpriseId.")
	
	return await fetchKwik(`/enterprises/${enterpriseId}/contact_enterprises/?pagination=False`)
}

export const createContact = async (enterpriseId, contactData) => {
	if (!enterpriseId)
		throw new Error("[createContact] Missing enterpriseId.")

	loader.on()
	try {
		return await fetchKwik(`/enterprises/${enterpriseId}/contacts/`, {
			method: "POST",
			body: JSON.stringify({
				"name": contactData.name,
				"nick": `+${contactData.phone_num}`,
				"telephone": `+${contactData.phone_num}`,
			})
		})	
	} finally {
		loader.off()	
	}
}

export const updateContact = async (enterpriseId, contactId, field, value) => {
	if (!enterpriseId || !contactId)
		throw new Error("[updateContact] Missing enterpriseId or contactId.")

	loader.on()
	try {
		await fetchKwik(`/enterprises/${enterpriseId}/contacts/${contactId}/`, {
			method: "PATCH",
			body: JSON.stringify({ [field]: value })
		})
	} finally {
		loader.off()
	}
}

// Pushes the CRM contact's current name into the Scout provider's own cache
// (kwik_contact_name), which is what conversation lists are read from on
// reload. Without this, a `updateContact` name change reverts after refresh.
export const resyncScoutContact = async (enterpriseId, accountId, conversationId) => {
	if (!enterpriseId || !accountId || !conversationId)
		throw new Error("[resyncScoutContact] Missing enterpriseId, accountId or conversationId.")

	return await fetchKwik(`/enterprises/${enterpriseId}/scout_accounts/${encodeURIComponent(accountId)}/resync_contact/${encodeURIComponent(conversationId)}/`)
}

export const createEnterprise = async (enterpriseId, name) => {
	if (!enterpriseId || !name)
		throw new Error("[createEnterprise] Missing enterpriseId or name.")

	loader.on()
	try {
		return await fetchKwik(`/enterprises/${enterpriseId}/contact_enterprises/`, {
			method: "POST",
			body: JSON.stringify({ name })
		})
	} finally {
		loader.off()	
	}
}

const getLegalName = async (cnpj) => {
	try {
		const parsedCnpj = cnpj.replace(/[^a-zA-Z0-9]/g, "")
		const response = await fetchKwik(`/cnpj/get_data/${parsedCnpj}/`)
		return response?.nome
	} catch (err) {
		return ""
	}
}

export const updateEnterpriseDocument = async (enterpriseId, documentType, documentValue) => {
	if (!enterpriseId || !documentType || !documentValue)
		throw new Error("[updateEnterpriseDocument] Missing enterpriseId or documentType or documentValue.")

	loader.on()
	
	const payload = (documentType === "cnpj") ? {
		cnpj: documentValue,
		legal_name: await getLegalName(documentValue),
	} : {
		cnpj: documentValue,
	}
	
	try {
		return await fetchKwik(`/enterprises/${enterpriseId}/update_document/`, {
			method: "PATCH",
			body: JSON.stringify(payload)
		})
	} finally {
		loader.off()
	}
}
